import axios, { type AxiosError } from 'axios';

/** The backend's RFC 9457 body. Mirrors GlobalExceptionHandler. */
interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  traceId?: string;
  errors?: Array<{ field: string; message: string }>;
}

export interface ApiError {
  /** 0 for network failures and cancellations, which have no HTTP status. */
  status: number;
  /** Stable machine code. Switch on this, never on `message`. */
  code: string;
  /** Safe to show a user. */
  message: string;
  /** Present on 400s. Feed straight into react-hook-form's setError. */
  fieldErrors?: Record<string, string>;
  /** Show in the unrecoverable error card so support can find the log line. */
  traceId?: string;
  retryable: boolean;
  cause?: unknown;
}

const DEFAULT_MESSAGE: Record<number, string> = {
  400: 'The request was not valid.',
  401: 'Your session has expired. Sign in again.',
  403: 'You do not have permission to do that.',
  404: 'That item could not be found.',
  409: 'That conflicts with the current state.',
  429: 'Too many requests. Try again shortly.',
  500: 'Something went wrong on our side.',
  503: 'The service is temporarily unavailable.',
};

export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'code' in error && 'retryable' in error;
}

/** A cancelled request is not a failure -- callers must not render an error for it. */
export const isCancelled = (error: unknown) => isApiError(error) && error.code === 'CANCELLED';

/**
 * Normalizes everything axios can throw into one shape, once, at the client boundary.
 *
 * Nothing above this layer should ever see a raw `AxiosError`: the moment one leaks,
 * every page grows its own `error.response?.data?.detail ?? error.message` expression
 * and they all disagree about what to show.
 */
export function toApiError(error: unknown): ApiError {
  if (axios.isCancel(error)) {
    return { status: 0, code: 'CANCELLED', message: '', retryable: false };
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ProblemDetail>;

    // No response at all: DNS failure, offline, CORS rejection, timeout.
    if (!axiosError.response) {
      return {
        status: 0,
        code: axiosError.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK',
        message: 'Cannot reach the server. Check your connection.',
        retryable: true,
        cause: error,
      };
    }

    const status = axiosError.response.status;
    const problem = axiosError.response.data;
    // A proxy or gateway can return HTML for a 502, so guard against a non-object body.
    const isProblem = typeof problem === 'object' && problem !== null;

    return {
      status,
      code: (isProblem && problem.code) || `HTTP_${status}`,
      message: (isProblem && problem.detail) || DEFAULT_MESSAGE[status] || 'Something went wrong.',
      fieldErrors:
        isProblem && Array.isArray(problem.errors) && problem.errors.length > 0
          ? Object.fromEntries(problem.errors.map((e) => [e.field, e.message]))
          : undefined,
      traceId: isProblem ? problem.traceId : undefined,
      // 4xx will fail again identically; 5xx and 429 may not.
      retryable: status >= 500 || status === 429,
      cause: error,
    };
  }

  return {
    status: 0,
    code: 'UNKNOWN',
    message: 'Something went wrong.',
    retryable: false,
    cause: error,
  };
}
